import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * StrategyAgent — Persistent right-side agent panel for legal & tax strategy.
 *
 * Props:
 *   strategyUuid: string
 *   strategyType: 'legal-strategy' | 'tax-strategy' (URL prefix)
 *   onRevisionComplete: () => void (refresh parent)
 */
function useIsMobile() {
  const [m, setM] = React.useState(typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => { const h = () => setM(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return m;
}

export default function StrategyAgent({ strategyUuid, strategyType, onRevisionStarted, onRevisionComplete }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [revising, setRevising] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const apiBase = `/api/${strategyType}/${strategyUuid}`;

  // Load conversation
  const loadConversation = useCallback(async () => {
    try {
      const r = await fetch(`${apiBase}/conversation`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) setMessages(d.questions || []);
    } catch {}
  }, [apiBase]);

  const loadVersions = useCallback(async () => {
    try {
      const r = await fetch(`${apiBase}/versions`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) setVersions(d.versions || []);
    } catch {}
  }, [apiBase]);

  useEffect(() => {
    if (strategyUuid) { loadConversation(); loadVersions(); }
  }, [strategyUuid, loadConversation, loadVersions]);

  useEffect(() => {
    if (open && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, open]);

  // Send message
  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/conversation`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      let d;
      try { d = await r.json(); } catch { d = { error: `Server returned ${r.status}` }; }
      if (d.success) {
        setMessages(prev => [...prev, d.question]);
        // Smart auto-revise: only if user requested a change and agent confirmed
        if (shouldAutoRevise(msg, d.question?.answer)) {
          console.log('Change detected — auto-updating strategy');
          setTimeout(() => doRevise(), 1000);
        }
      } else {
        setMessages(prev => [...prev, { question: msg, answer: `Error: ${d.error || 'Service temporarily unavailable. Please try again.'}` }]);
      }
    } catch (err) { console.error('Chat error:', err); }
    setLoading(false);
  };

  // Upload file
  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${apiBase}/upload`, { method: 'POST', credentials: 'include', body: fd });
      let d;
      try { d = await r.json(); } catch { d = { error: `Server returned ${r.status}: ${r.statusText}` }; }
      if (d.success) {
        setMessages(prev => [...prev, d.question]);
        // Auto-update strategy after file upload
        setTimeout(() => doRevise(), 1000);
      } else {
        // Show error as a system message
        setMessages(prev => [...prev, {
          question: `[Uploaded: ${file.name}]`,
          answer: `Upload failed: ${d.error || 'Unknown error'}. Please try again.`
        }]);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setMessages(prev => [...prev, {
        question: `[Uploaded: ${file.name}]`,
        answer: `Upload failed: ${err.message}. Please try again.`
      }]);
    }
    setUploading(false);
  };

  // Smart auto-revise: only when the user requests a change
  const CHANGE_PATTERNS = /\b(change|update|revise|modify|switch|replace|use .* instead|move to|convert|restructure|do .* instead|want .* instead|need .* instead|prefer|add|remove|set up|file|form|elect|incorporate)\b/i;

  const shouldAutoRevise = (userMessage, agentAnswer) => {
    // Check if user message contains change language
    if (!CHANGE_PATTERNS.test(userMessage)) return false;
    // Check if agent confirmed the change (not just answered a question)
    const confirmPatterns = /\b(absolutely|excellent|good (choice|decision|move)|makes sense|recommend|will (change|update|modify|switch)|revised|updated|implementing|changing)\b/i;
    return confirmPatterns.test(agentAnswer || '');
  };

  const doRevise = async () => {
    setRevising(true);
    try {
      const r = await fetch(`${apiBase}/revise`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (d.success) {
        loadVersions();
        // Tell parent to start polling
        if (onRevisionStarted) onRevisionStarted();
        // Also poll internally until strategy completes
        const pollInterval = setInterval(async () => {
          try {
            const pr = await fetch(`${apiBase}`, { credentials: 'include' });
            const pd = await pr.json();
            const status = pd.strategy?.status || pd.success && 'unknown';
            if (pd.strategy?.status === 'completed' || pd.strategy?.status === 'failed') {
              clearInterval(pollInterval);
              setRevising(false);
              loadVersions();
              if (onRevisionComplete) onRevisionComplete();
            }
          } catch {}
        }, 5000);
        // Timeout after 10 minutes
        setTimeout(() => { clearInterval(pollInterval); setRevising(false); }, 600000);
        return; // Don't set revising false yet
      }
    } catch (err) { console.error('Revise error:', err); }
    setRevising(false);
  };

  // Revise strategy (manual)
  const handleRevise = async () => {
    setShowConfirm(false);
    try {
      const r = await fetch(`${apiBase}/revise`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (d.success) {
        loadVersions();
        if (onRevisionComplete) onRevisionComplete();
      }
    } catch (err) { console.error('Revise error:', err); }
    setRevising(false);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) uploadFile(files[0]);
  };

  const currentVersion = versions.length + 1;
  const accentColor = strategyType === 'tax-strategy' ? '#059669' : '#4f46e5';

  if (!strategyUuid) return null;

  return (
    <>
      {/* Toggle button */}
      {!open && (
        isMobile ? (
          <button onClick={() => setOpen(true)}
            style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 30, width: 48, height: 48, borderRadius: '50%', border: 'none',
              background: accentColor, color: 'white', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            🤖
          </button>
        ) : (
          <div style={{ width: 40, flexShrink: 0, display: 'flex', alignItems: 'flex-start', paddingTop: 20 }}>
            <button onClick={() => setOpen(true)}
              style={{ padding: '10px 6px', borderRadius: '8px 0 0 8px', border: 'none',
                background: accentColor, color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                writingMode: 'vertical-rl', textOrientation: 'mixed', boxShadow: '-2px 0 6px rgba(0,0,0,0.1)' }}>
              Agent
            </button>
          </div>
        )
      )}

      {/* Agent Panel */}
      {open && (
        <div style={{ width: isMobile ? '100vw' : 380, flexShrink: 0, height: '100%',
          display: 'flex', flexDirection: 'column', background: 'white',
          borderLeft: isMobile ? 'none' : '1px solid #e2e8f0',
          position: isMobile ? 'fixed' : 'relative', inset: isMobile ? 0 : 'auto', zIndex: isMobile ? 50 : 'auto' }}
          onDragOver={handleDragOver} onDrop={handleDrop}>

          {/* Header */}
          <div style={{ padding: '12px 16px', background: accentColor, color: 'white', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                Strategy Agent
                <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.25)' }}>v{currentVersion}</span>
              </div>
              <div style={{ fontSize: 11, opacity: 0.8, marginTop: 1 }}>Ask questions, upload docs, revise strategy</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setShowVersions(!showVersions)}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: 6,
                  padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                History
              </button>
              <button onClick={() => setOpen(false)}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: 6,
                  padding: '4px 10px', cursor: 'pointer', fontSize: 13 }}>
                ✕
              </button>
            </div>
          </div>

          {/* Version History Panel */}
          {showVersions && (
            <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', maxHeight: 200, overflowY: 'auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#475569' }}>Version History</div>
              {versions.length === 0 ? (
                <p style={{ fontSize: 12, color: '#9ca3af' }}>No previous versions yet. Versions are created when you revise the strategy.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {versions.map(v => (
                    <div key={v.id}
                      onClick={() => setViewingVersion(viewingVersion?.id === v.id ? null : v)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6,
                        background: viewingVersion?.id === v.id ? '#eff6ff' : 'white', border: '1px solid #e2e8f0',
                        cursor: 'pointer', fontSize: 12 }}>
                      <span style={{ width: 24, height: 24, borderRadius: '50%', background: accentColor, color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        v{v.version_number}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.label}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(v.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 12 }}>
                    <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#059669', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                      v{currentVersion}
                    </span>
                    <span style={{ fontWeight: 500, color: '#059669' }}>Current version</span>
                  </div>
                </div>
              )}
              {viewingVersion && (
                <div style={{ marginTop: 8, padding: 10, background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>Viewing v{viewingVersion.version_number}</div>
                  <pre style={{ fontSize: 11, maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap', color: '#475569' }}>
                    {viewingVersion.results ? JSON.stringify(typeof viewingVersion.results === 'string' ? JSON.parse(viewingVersion.results) : viewingVersion.results, null, 2).substring(0, 1000) : 'No data'}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Related Files */}
          {messages.filter(m => m.question?.startsWith('[Uploaded:')).length > 0 && (
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Related Files</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {messages.filter(m => m.question?.startsWith('[Uploaded:')).map((m, i) => {
                  const match = m.question.match(/\[Uploaded: (.+?)\]/);
                  const name = match ? match[1] : 'File';
                  return (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px',
                      borderRadius: 4, background: '#ecfdf5', border: '1px solid #a7f3d0', fontSize: 11, color: '#065f46' }}>
                      📎 {name.length > 20 ? name.substring(0, 18) + '...' : name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Messages (drag-drop target) */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', position: 'relative' }}
            onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            {/* Drag overlay */}
            {dragOver && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(99,102,241,0.08)',
                border: `2px dashed ${accentColor}`, borderRadius: 8, display: 'flex',
                alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ padding: '14px 24px', background: 'white', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  fontSize: 14, fontWeight: 600, color: accentColor }}>
                  Drop file to analyze
                </div>
              </div>
            )}
            {messages.length === 0 && !loading && (
              <div style={{ textAlign: 'center', marginTop: 40 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🤖</div>
                <p style={{ color: '#9ca3af', fontSize: 13, lineHeight: 1.6 }}>
                  I'm your strategy agent. Ask me questions, drop files for analysis, or ask me to update the strategy.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 12 }}>
                  {['What are the risks?', 'How can I save more?', 'Compare approaches', 'Update the strategy'].map(s => (
                    <button key={s} onClick={() => { setInput(s); }}
                      style={{ padding: '4px 10px', borderRadius: 14, border: '1px solid #e2e8f0', background: 'white',
                        color: '#64748b', fontSize: 11, cursor: 'pointer' }}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || i} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                  <div style={{ maxWidth: '85%', padding: '8px 12px', fontSize: 13, lineHeight: 1.5,
                    background: m.question?.startsWith('[Uploaded:') ? '#ecfdf5' : accentColor,
                    color: m.question?.startsWith('[Uploaded:') ? '#065f46' : 'white',
                    borderRadius: '12px 12px 2px 12px' }}>
                    {m.question?.startsWith('[Uploaded:') ? '📎 ' : ''}{m.question}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-start', position: 'relative', group: true }}>
                  <div style={{ maxWidth: '85%', padding: '8px 12px', background: '#f3f4f6', borderRadius: '12px 12px 12px 2px',
                    fontSize: 13, lineHeight: 1.6, color: '#333', position: 'relative' }}>
                    <button onClick={() => { navigator.clipboard.writeText(m.answer); const btn = document.getElementById('copy-' + i); if (btn) { btn.textContent = '✓'; setTimeout(() => btn.textContent = '📋', 1500); } }}
                      id={'copy-' + i}
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(255,255,255,0.8)', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', fontSize: 11, opacity: 0.5, transition: 'opacity 0.2s' }}
                      onMouseEnter={e => e.target.style.opacity = 1}
                      onMouseLeave={e => e.target.style.opacity = 0.5}
                      title="Copy to clipboard">📋</button>
                    <div className="agent-markdown">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.answer}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8 }}>
                <div style={{ padding: '12px 16px', background: '#f3f4f6', borderRadius: 12, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0s' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0.2s' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0.4s' }} />
                  </div>
                  <span style={{ fontSize: 13, color: '#9ca3af', fontWeight: 500 }}>Thinking...</span>
                </div>
              </div>
            )}
            {uploading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8 }}>
                <div style={{ padding: '12px 16px', background: '#ecfdf5', borderRadius: 12, border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#059669', animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0s' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#059669', animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0.2s' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#059669', animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0.4s' }} />
                  </div>
                  <span style={{ fontSize: 13, color: '#059669', fontWeight: 500 }}>Analyzing document...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Update Strategy */}
          {messages.length > 0 && (
            <div style={{ padding: '6px 16px', borderTop: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}>
              {revising ? (
                <>
                  <span style={{ display: 'flex', gap: 3 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: accentColor, animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0s' }} />
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: accentColor, animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0.2s' }} />
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: accentColor, animation: 'agentBounce 1.4s ease-in-out infinite', animationDelay: '0.4s' }} />
                  </span>
                  <span style={{ fontSize: 12, color: accentColor, fontWeight: 500 }}>Updating strategy...</span>
                </>
              ) : (
                <button onClick={() => doRevise()}
                  style={{ padding: '5px 14px', borderRadius: 6, border: `1px solid ${accentColor}`, background: 'white',
                    color: accentColor, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  ✨ Update Strategy
                </button>
              )}
            </div>
          )}

          {/* Input Bar */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            <input type="file" ref={fileInputRef} accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp" style={{ display: 'none' }}
              onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              style={{ padding: '7px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white',
                color: uploading ? '#9ca3af' : accentColor, cursor: 'pointer', fontSize: 14, flexShrink: 0 }}>
              📎
            </button>
            <input type="text" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
              placeholder="Ask a question or drop a file..."
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6,
                fontSize: 16, outline: 'none' }} />
            <button onClick={sendMessage} disabled={!input.trim() || loading}
              style={{ padding: '8px 14px', borderRadius: 6, border: 'none',
                background: (!input.trim() || loading) ? '#d1d5db' : accentColor,
                color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
              Send
            </button>
          </div>
        </div>
      )}
      <style>{`
        @keyframes agentBounce {
          0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
        .agent-markdown { font-size: 13px; line-height: 1.6; }
        .agent-markdown p { margin: 0 0 8px; }
        .agent-markdown p:last-child { margin-bottom: 0; }
        .agent-markdown h1, .agent-markdown h2, .agent-markdown h3 { font-weight: 700; margin: 10px 0 4px; }
        .agent-markdown h1 { font-size: 15px; }
        .agent-markdown h2 { font-size: 14px; }
        .agent-markdown h3 { font-size: 13px; }
        .agent-markdown strong { font-weight: 600; }
        .agent-markdown ul, .agent-markdown ol { margin: 4px 0 8px 16px; padding: 0; }
        .agent-markdown li { margin-bottom: 2px; }
        .agent-markdown code { background: #f1f5f9; padding: 1px 4px; border-radius: 3px; font-size: 12px; }
        .agent-markdown pre { background: #1e293b; color: #e2e8f0; padding: 10px; border-radius: 6px; overflow-x: auto; font-size: 12px; margin: 6px 0; }
        .agent-markdown pre code { background: none; padding: 0; color: inherit; }
        .agent-markdown blockquote { border-left: 3px solid #d1d5db; margin: 6px 0; padding: 4px 12px; color: #64748b; }
        .agent-markdown table { border-collapse: collapse; width: 100%; margin: 6px 0; font-size: 12px; }
        .agent-markdown th, .agent-markdown td { border: 1px solid #e2e8f0; padding: 4px 8px; text-align: left; }
        .agent-markdown th { background: #f8fafc; font-weight: 600; }
        .agent-markdown hr { border: none; border-top: 1px solid #e2e8f0; margin: 8px 0; }
        .agent-markdown a { color: #4f46e5; text-decoration: none; }
        .agent-markdown a:hover { text-decoration: underline; }
      `}</style>
    </>
  );
}
